import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, PackageOpen, ArrowRight } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function displayStatus(status, bidCount) {
  if (status === "open") return bidCount > 0 ? "Bidding" : "Open";
  if (status === "assigned") return "Accepted";
  if (status === "completed") return "Completed";
  if (status === "expired") return "Expired";
  if (status === "cancelled") return "Cancelled";
  return status;
}

const STATUS_CLS = {
  Open: "bg-[#FF3B30] text-white",
  Bidding: "bg-amber-500/90 text-black",
  Accepted: "bg-emerald-500/90 text-black",
  Completed: "bg-white/10 text-zinc-300",
  Expired: "bg-zinc-800 text-zinc-500",
  Cancelled: "bg-zinc-800 text-zinc-500",
};

function StatusCell({ status, bidCount, testid }) {
  const label = displayStatus(status, bidCount);
  return (
    <span
      data-testid={testid}
      className={`inline-block text-[10px] uppercase tracking-[0.2em] font-black px-2 py-1 ${STATUS_CLS[label] || STATUS_CLS.Open}`}
    >
      {label}
    </span>
  );
}

export default function AdminRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // request id
  const [bids, setBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async (status = statusFilter, q = search) => {
    try {
      const { data } = await api.get("/admin/requests", { params: { status, q } });
      setRows(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // debounced shipper-email search
  useEffect(() => {
    const t = setTimeout(() => load(statusFilter, search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggle = async (reqId) => {
    if (expanded === reqId) {
      setExpanded(null);
      return;
    }
    setExpanded(reqId);
    setBidsLoading(true);
    try {
      const { data } = await api.get(`/requests/${reqId}/bids`);
      data.sort((a, b) => b.price_usd - a.price_usd);
      setBids(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
      setBids([]);
    } finally {
      setBidsLoading(false);
    }
  };

  if (user && user.role !== "admin") {
    return <div className="p-14 text-red-400 uppercase text-sm">Admin only.</div>;
  }

  const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-7xl mx-auto" data-testid="admin-requests-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          Shipper requests
        </h1>
        <p className="text-sm text-zinc-500 mt-3" data-testid="admin-requests-count">
          {rows.length} load{rows.length === 1 ? "" : "s"} {statusFilter || search ? "match your filters" : "in the system"} — click a row to see every driver bid.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-1.5 flex-wrap" data-testid="request-status-filters">
          {["", "open", "bidding", "accepted", "completed", "expired", "cancelled"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`req-filter-${s || "all"}`}
              className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
                statusFilter === s
                  ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                  : "border-white/10 text-zinc-400 hover:border-white"
              }`}
            >
              {s || "All"}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by shipper email…"
          data-testid="req-search"
          className="flex-1 min-w-[220px] max-w-sm bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 py-2 px-3 text-sm"
        />
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading requests…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-white/[0.08] p-12 text-center bg-[#0F0F10]">
          <PackageOpen className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <div className="font-display text-2xl font-black uppercase">No requests yet</div>
          <p className="text-zinc-500 text-sm mt-2">Loads posted by shippers will land here.</p>
        </div>
      ) : (
        <div className="border border-white/[0.08] bg-[#0F0F10] overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold text-left">
                <th className="px-4 py-3">Shipper</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Origin → Destination</th>
                <th className="px-4 py-3">Pickup</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Bids</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    onClick={() => toggle(r.id)}
                    data-testid={`request-row-${r.id}`}
                    className={`cursor-pointer transition-colors ${expanded === r.id ? "bg-[#FF3B30]/[0.06]" : "hover:bg-white/[0.03]"}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {expanded === r.id ? (
                          <ChevronUp className="w-3.5 h-3.5 text-[#FF3B30] shrink-0" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                        )}
                        <div>
                          <div className="font-semibold text-white">{r.shipper_name}</div>
                          <div className="text-[11px] text-zinc-500 truncate max-w-[180px]">{r.title}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{r.shipper_email}</td>
                    <td className="px-4 py-3 text-zinc-300">{r.shipper_phone || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">
                      {r.pickup_city} <ArrowRight className="inline w-3 h-3 text-zinc-600" /> {r.delivery_city}
                    </td>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{fmtDate(r.haul_start)}</td>
                    <td className="px-4 py-3">
                      <StatusCell status={r.status} bidCount={r.bid_count} testid={`request-status-${r.id}`} />
                    </td>
                    <td className="px-4 py-3 text-right font-display font-black text-lg" data-testid={`request-bidcount-${r.id}`}>
                      {r.bid_count}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={7} className="px-4 py-4 bg-black/40">
                        <div data-testid={`request-bids-${r.id}`}>
                          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-3">
                            Driver bids — {r.title}
                          </div>
                          {bidsLoading ? (
                            <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading bids…</div>
                          ) : bids.length === 0 ? (
                            <div className="text-zinc-500 text-sm">No bids on this load yet.</div>
                          ) : (
                            <div className="space-y-2">
                              {bids.map((b) => (
                                <div
                                  key={b.id}
                                  data-testid={`admin-bid-row-${b.id}`}
                                  className="flex items-center justify-between gap-4 border border-white/[0.06] bg-[#0F0F10] px-4 py-3"
                                >
                                  <div className="min-w-0">
                                    <div className="font-semibold text-white">{b.driver_name}</div>
                                    <div className="text-[11px] text-zinc-500">
                                      {new Date(b.created_at).toLocaleString()}
                                      {b.message ? ` · "${b.message.slice(0, 80)}${b.message.length > 80 ? "…" : ""}"` : ""}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 shrink-0">
                                    <span
                                      className={`text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 ${
                                        b.status === "accepted"
                                          ? "bg-emerald-500/90 text-black"
                                          : b.status === "pending"
                                          ? "bg-white/10 text-zinc-300"
                                          : "bg-zinc-800 text-zinc-500"
                                      }`}
                                    >
                                      {b.status}
                                    </span>
                                    <span className="font-display text-xl font-black">${b.price_usd.toLocaleString()}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
